import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Printer, Tag } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function Labels() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [overrides, setOverrides] = useState({ price: "", batch: "", expiry: "", quantity: 12 });

  useEffect(() => { (async () => { const { data } = await api.get("/products"); setProducts(data); })(); }, []);

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);
  const qrPayload = useMemo(() => {
    if (!product) return "";
    return JSON.stringify({
      sku: product.sku,
      bar: product.barcode || product.sku,
      lot: overrides.batch || "",
      exp: overrides.expiry || "",
      px: Number(overrides.price || product.price),
    });
  }, [product, overrides]);

  const printNow = () => window.print();

  return (
    <div className="space-y-6 print:hidden">
      <PageHeader title="Étiquettes médicaments" subtitle="Imprimables · QR Code"
        actions={product && <button data-testid="print-labels" onClick={printNow} className="btn-primary flex items-center gap-2"><Printer size={14}/>Imprimer</button>}
      />

      <Surface title="Configuration">
        <div className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="label-mono block mb-1.5">Produit</label>
            <select data-testid="label-product" className="input-base" value={productId} onChange={(e) => setProductId(e.target.value)}>
              <option value="">— Choisir —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
            </select>
          </div>
          <div><label className="label-mono block mb-1.5">Prix (F)</label><input className="input-base" placeholder={product?.price || "—"} value={overrides.price} onChange={(e) => setOverrides({ ...overrides, price: e.target.value })}/></div>
          <div><label className="label-mono block mb-1.5">Lot</label><input className="input-base" placeholder="LOT-2026-A" value={overrides.batch} onChange={(e) => setOverrides({ ...overrides, batch: e.target.value })}/></div>
          <div><label className="label-mono block mb-1.5">Péremption</label><input type="date" className="input-base" value={overrides.expiry} onChange={(e) => setOverrides({ ...overrides, expiry: e.target.value })}/></div>
          <div><label className="label-mono block mb-1.5">Qté étiquettes</label><input type="number" min="1" max="100" className="input-base" value={overrides.quantity} onChange={(e) => setOverrides({ ...overrides, quantity: e.target.value })}/></div>
        </div>
      </Surface>

      {!product ? (
        <Surface><Empty message="Sélectionnez un produit pour générer les étiquettes"/></Surface>
      ) : (
        <Surface title={`Aperçu — ${product.name}`}>
          <div id="labels-print-area" className="p-6 bg-white text-black grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="labels-grid">
            {Array.from({ length: Number(overrides.quantity || 1) }).map((_, i) => (
              // index OK ici : N copies identiques de la même étiquette, pas de réordonnancement
              <div key={`label-${i}`} className="border border-black/30 p-2 flex gap-2 items-start break-inside-avoid" style={{ pageBreakInside: "avoid" }}>
                <div className="shrink-0">
                  <QRCodeSVG value={qrPayload} size={56} bgColor="#fff" fgColor="#000" level="M"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-wider uppercase text-black/60">{product.sku}</div>
                  <div className="font-bold text-xs leading-tight truncate">{product.name}</div>
                  <div className="text-[10px] mt-0.5">Prix: <span className="font-bold">{Number(overrides.price || product.price).toLocaleString("fr-FR")} F</span></div>
                  {overrides.batch && <div className="text-[10px]">Lot: {overrides.batch}</div>}
                  {overrides.expiry && <div className="text-[10px]">Exp: {overrides.expiry}</div>}
                  <div className="text-[8px] text-black/50 mt-0.5 flex items-center gap-1"><Tag size={8}/>Vénus Médical</div>
                </div>
              </div>
            ))}
          </div>
        </Surface>
      )}

      <style>{`
        @media print {
          body { background: #fff !important; }
          .print\\:hidden { display: block !important; }
          [data-testid="sidebar"], [data-testid="page-header"], button, .label-mono, .surface > div:first-child { display: none !important; }
          #labels-print-area { padding: 0 !important; }
          .grid-bg, .scanlines { background: none !important; }
        }
      `}</style>
    </div>
  );
}
